import { useCallback, useEffect, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components/macro";
import { InvisibleButton, LinkButtonSmallV2 } from "../Buttons/Buttons";
import type { ChipType } from "../Chips/Chips";
import { getFilterItemName, useStoreState } from "../../util/util";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";
import { CheckBoxNoLabel } from "../CheckBoxes/CheckBoxes";
import { SoftHeader2 } from "../Typography/Typography";
import { XIcon } from "../Icons/Icons";
import { SearchBar } from "../SearchBar/SearchBar";
import type {
  Filters,
  NumericFilterCriterion,
  NumericFilterOperator,
  SupportedLanguage,
  TagClassificationConfig,
} from "../../types/types";
import type { EntityType } from "../../types/types.MDM";
import { Loader } from "../Loader/Loader";
import { LoaderContainer } from "../../pages/public/pages.public.util";
import { StringParam, useQueryParams } from "use-query-params";
import { useCookies } from "react-cookie";
import _ from "lodash";
import { CheckBoxFinePrintLabel } from "../Form/Form";
import { spacingTokens } from "../../designTokens";
import {
  decodeNumericFilterCriterion,
  encodeNumericFilterCriterion,
  isSameFacetOption,
} from "./filterutils";
import { NumericFilterInput } from "./NumericFilterInput";

export interface AnimatedExpandToggleProps {
  expanded: boolean;
}

export const FilterGroupWrapper = styled.div<{ expanded: boolean }>`
  display: block;
  width: 100%;
  position: relative;
  margin: ${({ theme }) => theme.resolve("spacing_none")};
  border-bottom: ${({ theme, expanded }) =>
    expanded
      ? `${theme.resolve("border_filterGroup") || 1} solid ${
          theme.resolve("color_filterGroup_border_expanded") ||
          theme.primaryBorder
        }`
      : `${theme.resolve("border_filterGroup") || 1} solid ${
          theme.resolve("color_filterGroup_border_collpased") ||
          theme.primaryBorder
        }`};
  button {
    cursor: pointer;
  }
  transition: max-height 0.25s ease-in;
`;

export const FilterGroupHeader = styled.div<{ expanded: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  justify-content: space-between;
  padding: ${({ theme }) =>
    ` ${theme.resolve("spacing_md")} ${theme.resolve("spacing_xl")}`};
  font-size: ${({ theme }) => theme.fontSizes.small};
  font-weight: ${({ theme }) => theme.resolve("font_weight_medium")};
  background: ${({ theme, expanded }) =>
    expanded
      ? theme.resolve("color_filterGroup_bg_expanded")
      : theme.resolve("color_filterGroup_bg_collpased")};
`;

const FilterGroupContent = styled.div`
  background: ${({ theme }) => theme.resolve("color_filterGroup_bg_expanded")};
`;

export const SearchBarWrapper = styled.div`
  padding: ${({ theme }) => ` 0 ${theme.resolve("spacing_xl")}`};
  margin-bottom: 10px;
  input {
    border-radius: ${({ theme }) => theme.resolve("radius_xs")};
    padding: 10px 13px 10px 35px;
    background: ${({ theme }) => theme.primaryBG};
  }
  svg {
    left: 7px;
    path {
      fill: #000;
    }
  }
`;

const NoSearchResults = styled.div`
  width: 100%;
  padding: ${({ theme }) => theme.resolve("spacing_2xl")} 0;
  text-align: center;
`;

const ClearFiltersWrapper = styled.div`
  padding: ${({ theme }) => theme.resolve("spacing_none")}
    ${({ theme }) => theme.resolve("spacing_none")}
    ${({ theme }) => theme.resolve("spacing_none")}
    ${({ theme }) => theme.resolve("spacing_sm")};
  margin-right: 5px;
  display: flex;
  flex-direction: row;
  align-items: center;
  display: flex;
  border-radius: 3px;
  height: 25px;
  font-size: ${({ theme }) => theme.fontSizes.small};
  line-height: ${({ theme }) => theme.fontSizes.small};
  background: ${({ theme }) => theme.primaryBG};
  border: ${({ theme }) => theme.resolve("border_md")} solid
    ${({ theme }) => theme.resolve("color_filterPanel_border")};
  button {
    display: flex;
    align-items: center;
  }
`;

export const AnimatedExpandToggle = styled.div<AnimatedExpandToggleProps>`
  position: relative;
  width: 25px;
  height: 24px;
  cursor: pointer;
  background: ${({ theme }) => theme.primaryBG};
  &:hover {
    transform: scale(1);
  }
  &:before,
  &:after {
    content: "";
    display: block;
    background-color: #333;
    position: absolute;
    top: 12px;
    left: 6px;
    transition: 0.35s;
    width: 12px;
    height: 2px;
  }
  &:after {
    width: 13px;
  }
  &:before {
    transform: ${({ expanded }) =>
      expanded ? "translatey(-50%)" : "translatey(-50%) rotate(-90deg)"};
    opacity: ${({ expanded }) => (expanded ? "0" : "1")};
    top: ${({ expanded }) => (expanded ? "12px" : "13px")};
    width: ${({ expanded }) => (expanded ? "12px" : "11px")};
    left: ${({ expanded }) => (expanded ? "6px" : "7px")};

    &:after {
      transform: ${({ expanded }) =>
        expanded
          ? "translatey(-50%) rotate(90deg)"
          : "translatey(-50%) rotate(0)"};
    }
  }
`;

export const ScrollUL = styled.div`
  max-height: 350px;
  overflow-y: auto;
  display: block;
  padding: ${({ theme }) => theme.resolve("spacing_md")}
    ${({ theme }) => theme.resolve("spacing_none")} 5px
    ${({ theme }) => theme.resolve("spacing_none")};
  transition: max-height 0.3s ease-in;
  li {
    list-style: none;
    padding: ${({ theme }) => ` 0 ${theme.resolve("spacing_xl")}`};
    margin-bottom: ${({ theme }) => theme.resolve("spacing_filterItem_gap")};
    cursor: pointer;
    display: flex;
    flex-direction: row;
    align-items: center;
    transition: all 0.3s ease-in;
    input[type="checkbox"] {
      width: 24px;
      margin-right: ${({ theme }) =>
        theme.resolve("spacing_filterPanel")} !important;
    }
  }
`;

const SelectAllButton = styled(LinkButtonSmallV2)`
  padding: ${({ theme }) => `0 ${theme.resolve("spacing_xl")}`};
`;

interface IFilterBy {
  setter: ({ values, filter }: { values?: ChipType[]; filter: string }) => void;
  options?: ChipType[];
  filters?: Filters;
  filterType: string;
  customLabels: TagClassificationConfig[];
  preselectedFilters: ChipType[];
  expanded: boolean;
  setActiveTagView: (tagView: string) => void;
  entityType?: EntityType;
  /**
   * AP-1016 (Advanced Filters, Part 1) — presence (and non-empty-ness) of
   * this prop is what marks a `FilterGroup` instance as a numeric filter
   * (see docs/plans/NUMERIC_FILTERS_FE_SPEC.md): its operator dropdown is
   * populated from this list, sourced from the backend's `numeric_filters`
   * response map — never hardcoded. `options`/`filters` are unused for a
   * numeric instance.
   */
  numericOperators?: NumericFilterOperator[];
}

/**
 * Reads in filters from the URL if they exist and applies them the list.
 * selectedFilters will update when a user clicks on the checkbox. When that array changes the request will run.
 */
export const FilterGroup = ({
  setter,
  options,
  filterType,
  preselectedFilters,
  customLabels,
  filters,
  expanded,
  setActiveTagView,
  entityType,
  numericOperators,
}: IFilterBy) => {
  const [pendingFilters, setPendingFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );

  const [selectedFilters, setSelectedFilters] = useState<ChipType[]>(
    preselectedFilters || []
  );

  const [query, setQuery] = useQueryParams({
    active_tag_view: StringParam,
  });

  const [showList, setShowList] = useState(expanded);
  const [canSelectFilter, setCanSelectFilter] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const theme = useTheme();

  useEffect(() => {
    setShowList(expanded);
  }, [expanded]);

  const { t } = useTranslation();
  const { getTemplateDisplayName } = useMdmEntityDisplayNames();

  const clear = useCallback(() => {
    // useCallback is needed here because this a dep of the
    // below useEffect
    setPendingFilters([]);
    setSelectedFilters([]);
    setter({ values: undefined, filter: filterType });
  }, [setter, filterType]);

  // AP-1016 (Advanced Filters, Part 1) — render the operator+value UI
  // instead of the checkbox list when this instance represents a numeric
  // filter (see `numericOperators`'s doc comment above).
  const isNumeric = !!numericOperators && numericOperators.length > 0;

  const numericCriterion = useMemo(
    () => decodeNumericFilterCriterion(preselectedFilters?.[0]?.name),
    [preselectedFilters]
  );

  // Numeric criteria apply immediately on "Apply" rather than going through
  // the checkbox list's 1500ms debounce below, which exists to let a user
  // click several checkboxes before firing a request.
  const handleNumericApply = useCallback(
    (criterion: NumericFilterCriterion) => {
      const chip: ChipType = {
        name: encodeNumericFilterCriterion(criterion),
        type: "numeric",
      };
      setPendingFilters([chip]);
      setSelectedFilters([chip]);
      setter({ values: [chip], filter: filterType });
      if (query.active_tag_view !== filterType) {
        setQuery({ active_tag_view: filterType });
      }
    },
    [setter, filterType, query.active_tag_view, setQuery]
  );

  // Memoize filtered options based on search query
  const filteredOptionsBySearch = useMemo(() => {
    if (!searchQuery) return options || [];
    const lowerQuery = searchQuery.toLowerCase();
    return (options || []).filter((item) => {
      const displayName =
        filterType === "template"
          ? getTemplateDisplayName({
              template_name: item.name,
              template_type: entityType,
            })
          : item.name;
      return displayName.toLowerCase().includes(lowerQuery);
    });
  }, [options, searchQuery, filterType, entityType, getTemplateDisplayName]);

  const handleFilterClick = useCallback(
    (option: ChipType, isChecked: boolean) => {
      if (isChecked) {
        // Preserve id + type so id-keyed facets (e.g. `user`) apply by id.
        setPendingFilters([
          ...pendingFilters,
          { name: option.name, id: option.id, type: option.type },
        ]);
      } else {
        if (pendingFilters.length > 1) {
          setPendingFilters((prev) =>
            prev.filter((item) => !isSameFacetOption(item, option))
          );
        } else {
          clear();
        }
      }
    },
    [pendingFilters, clear]
  );

  const handleSelectAll = useCallback(() => {
    setPendingFilters(filteredOptionsBySearch || []);
  }, [filteredOptionsBySearch]);

  const is_all_selected = useMemo(() => {
    return (
      pendingFilters.length > 0 &&
      (filteredOptionsBySearch || []).length > 0 &&
      (filteredOptionsBySearch || []).every((option) =>
        pendingFilters.some((filter) => isSameFacetOption(filter, option))
      )
    );
  }, [pendingFilters, filteredOptionsBySearch]);

  const { slug: tenantSlug } = useStoreState();

  const [cookies] = useCookies([`preferred-language-${tenantSlug}`]);
  const preferredLanguage: SupportedLanguage | undefined = cookies[
    `preferred-language-${tenantSlug}`
  ] as SupportedLanguage;

  const handleChange = useCallback(
    (val: ChangeEvent<HTMLInputElement>) => setSearchQuery(val.target.value),
    []
  );

  // Memoize the label calculation
  const label = useMemo(() => {
    const getCustomLabel = (filterType: string) => {
      const customLabel = customLabels
        ?.find((customLabel) => customLabel.filter_type === filterType)
        ?.filter_labels?.find(
          (filterLabel) => filterLabel.language === (preferredLanguage ?? "en")
        )?.label;
      return customLabel ? customLabel : getFilterItemName(filterType);
    };
    return getCustomLabel(filterType);
  }, [filterType, customLabels, preferredLanguage]);

  const handleClearInput = useCallback(() => setSearchQuery(""), []);

  const getFilterName = useCallback(
    (filter: ChipType) => {
      const displayName =
        filterType === "template"
          ? getTemplateDisplayName({
              template_name: filter.name,
              template_type: entityType,
            })
          : filter.name;
      if (searchQuery.length > 0) {
        const lowerName = displayName.toLowerCase();
        const lowerQuery = searchQuery.toLowerCase();
        const start = lowerName.indexOf(lowerQuery);

        if (start > -1) {
          const end = start + searchQuery.length;
          return (
            <>
              {displayName.substring(0, start)}
              <b>{displayName.substring(start, end)}</b>
              {displayName.substring(end, displayName.length)}
            </>
          );
        }
      }
      return displayName;
    },
    [searchQuery, filterType, entityType, getTemplateDisplayName]
  );

  // Consolidate preselected filters effect
  useEffect(() => {
    if (preselectedFilters) {
      setSelectedFilters(preselectedFilters);
      setPendingFilters(preselectedFilters);
    }
  }, [preselectedFilters]);

  useEffect(() => {
    setCanSelectFilter(true);
  }, [filters]);

  // Optimize the debounced filter application
  useEffect(() => {
    if (!_.isEqual(pendingFilters, selectedFilters)) {
      const delayApplyFilters = setTimeout(() => {
        setSelectedFilters(pendingFilters);
        setter({
          values: pendingFilters,
          filter: filterType,
        });
        if (query.active_tag_view !== filterType) {
          setQuery({ active_tag_view: filterType });
        }
        setCanSelectFilter(false);
      }, 1500);

      return () => clearTimeout(delayApplyFilters);
    }
  }, [
    pendingFilters,
    setter,
    filterType,
    selectedFilters,
    query.active_tag_view,
    setQuery,
  ]);

  // Memoize click handlers
  const handleExpandToggleClick = useCallback(() => {
    setShowList(!showList);
    setActiveTagView(filterType);
  }, [filterType, setActiveTagView, showList]);

  const handleClearClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      clear();
    },
    [clear]
  );

  return (
    <FilterGroupWrapper expanded={showList}>
      <FilterGroupHeader
        // active={appliedFilters.length > 0 || showList}
        expanded={showList}
      >
        <div
          onClick={handleExpandToggleClick}
          style={{
            width: "100%",
            padding: `${spacingTokens.spacing_md} ${spacingTokens.spacing_xxs} ${spacingTokens.spacing_md} ${spacingTokens.spacing_none}`,
          }}
        >
          {label.charAt(0).toUpperCase()}
          {label.slice(1)}
        </div>
        <div style={{ display: "flex" }}>
          {selectedFilters.length > 0 && (
            <ClearFiltersWrapper>
              <div> {selectedFilters.length}</div>
              <InvisibleButton onClick={handleClearClick}>
                <XIcon width={15} height={15} />
              </InvisibleButton>
            </ClearFiltersWrapper>
          )}
          {
            <AnimatedExpandToggle
              style={{
                padding: `${spacingTokens.spacing_md} ${spacingTokens.spacing_md} ${spacingTokens.spacing_md} ${spacingTokens.spacing_xxs}`,
                background: theme.primaryBG,
              }}
              expanded={showList}
              onClick={handleExpandToggleClick}
            />
          }
        </div>
      </FilterGroupHeader>
      {showList && isNumeric && numericOperators && (
        <FilterGroupContent>
          <NumericFilterInput
            filterType={filterType}
            operators={numericOperators}
            initialCriterion={numericCriterion}
            onApply={handleNumericApply}
            onClear={clear}
          />
        </FilterGroupContent>
      )}
      {showList && !isNumeric && (
        <FilterGroupContent>
          <SearchBarWrapper>
            <SearchBar
              placeHolder={`Search ${label
                .charAt(0)
                .toUpperCase()}${label.slice(1)}`}
              query={searchQuery}
              handleChange={handleChange}
              handleClearInput={handleClearInput}
            />
          </SearchBarWrapper>
          {!filters && (
            <LoaderContainer style={{ marginTop: "30px" }}>
              <Loader isLoading={true} width={22} />
            </LoaderContainer>
          )}

          {filters && (
            <>
              <SelectAllButton
                onClick={is_all_selected ? clear : handleSelectAll}
                disabled={!canSelectFilter}
              >
                {is_all_selected ? t("Unselect All") : t("Select All")}
              </SelectAllButton>
              <ScrollUL>
                {filteredOptionsBySearch.map((option, index) => (
                  <li key={index}>
                    <CheckBoxNoLabel
                      name={`is_filter_checked${index}`}
                      id={`is_filter_checked${filterType}-${index}`}
                      checked={pendingFilters.some((i) =>
                        isSameFacetOption(i, option)
                      )}
                      onChange={(e) =>
                        handleFilterClick(option, e.currentTarget.checked)
                      }
                      disabled={!canSelectFilter}
                    />
                    <CheckBoxFinePrintLabel
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        width: "100%",
                        cursor: !canSelectFilter ? "not-allowed" : "pointer",
                      }}
                      htmlFor={`is_filter_checked${filterType}-${index}`}
                    >
                      <span
                        style={{
                          color: theme.resolve("color_text_secondary"),
                          marginRight: theme.resolve("spacing_md"),
                        }}
                      >
                        {getFilterName(option)}
                      </span>
                      {
                        <span
                          style={{
                            color: theme.resolve("color_text_tertiary"),
                          }}
                        >
                          {(option as { name: string; count: number }).count}
                        </span>
                      }
                    </CheckBoxFinePrintLabel>
                  </li>
                ))}
                {filteredOptionsBySearch.length === 0 && (
                  <NoSearchResults>
                    <SoftHeader2>{t("No results found")}</SoftHeader2>
                  </NoSearchResults>
                )}
              </ScrollUL>
            </>
          )}
        </FilterGroupContent>
      )}
    </FilterGroupWrapper>
  );
};

FilterGroup.displayName = "FilterGroup";
